import { lazy } from 'react'

import ErrorBoundary from 'tools/ErrorBoundary'
import { joinPath } from 'utils/string'

import { AuthPassword, AuthSignOn, AuthSignOut } from 'app/Auth'
import { NavOutlet } from 'app/Nav'
import { buildConfigs, genNavPoint, genRedirect, tabMenus } from 'app/Nav/lib'

const Home = lazy(() => import('./Static/Home'))
const Project = lazy(() => import('libreon/Project'))
const ProjectFind = lazy(() => import('libreon/Project/Find'))
const ProjectMine = lazy(() => import('libreon/Project/Find/Mine'))
const ProjectView = lazy(() => import('libreon/Project/View'))
const ProjectCreate = lazy(() => import('libreon/Project/Create'))
const ProjectDiscussGeneral = lazy(
  () => import('libreon/Project/Discuss/General')
)
const ProjectDiscussClub = lazy(() => import('libreon/Project/Discuss/Club'))
const ProjectEdit = lazy(() => import('libreon/Project/Edit'))
const ProjectProfile = lazy(() => import('libreon/Project/Edit/Profile'))
const ProjectFeatures = lazy(() => import('libreon/Project/Edit/Features'))
// const ProjectFiles = lazy(() => import('libreon/Project/Edit/Files'))
const ProjectGuides = lazy(() => import('libreon/Project/Edit/Guides'))
const ProjectJourneys = lazy(() => import('libreon/Project/Edit/Journeys'))
const ProjectChallengeInputGroup = lazy(
  () => import('libreon/Project/Edit/Journeys/ChallengeInputGroup')
)
// const ProjectEditClub = lazy(() => import('libreon/Project/Edit/Club'))
const ProjectMembers = lazy(() => import('libreon/Project/Edit/Members'))
const ProjectAdvisory = lazy(() => import('libreon/Project/Edit/Advisory'))

const Doc0Page = lazy(() => import('app/Doc0/Page'))
const ContentAdvisory = lazy(() => import('libreon/Static/ContentAdvisory'))
const ContentGuide = lazy(() => import('libreon/Static/ContentGuide'))
const Terms = lazy(() => import('libreon/Static/Terms'))

const User = lazy(() => import('libreon/User'))
const UserAccount = lazy(() => import('libreon/User/Edit/Account'))
const UserDelete = lazy(() => import('libreon/User/Edit/Account/DeleteAccount'))
const UserContact = lazy(() => import('libreon/User/Edit/Contact'))
const UserSubscriptions = lazy(() => import('libreon/User/Edit/Subscriptions'))
const UserMessages = lazy(() => import('libreon/User/Edit/Messages'))
// const UserPosts = lazy(() => import('libreon/User/Edit/Posts'))
const UserProfile = lazy(() => import('libreon/User/Edit/Profile'))

// const AuthPassword = lazy(() => import('app/Auth/Password'))
// const AuthSignOn = lazy(() => import('app/Auth/SignOn'))
// const AuthSignOut = lazy(() => import('app/Auth/SignOut'))
const Explore = lazy(() => import('libreon/Explore'))
const ExploreLearn = lazy(() => import('libreon/Library'))
const LearnEdit = lazy(() => import('libreon/Library/EditTopic'))
const LearnView = lazy(() => import('libreon/Library/ViewTopic'))

const Meet = lazy(() => import('libreon/Meet'))
const MeetEdit = lazy(() => import('libreon/Meet/Edit'))
const MeetEditProfile = lazy(() => import('libreon/Meet/Edit/Profile'))
const MeetEditFiles = lazy(() => import('libreon/Meet/Edit/Files'))
const MeetEditEventList = lazy(() => import('libreon/Meet/Edit/Event/List'))
const MeetEditEvent = lazy(() => import('libreon/Meet/Edit/Event'))
const MeetFind = lazy(() => import('libreon/Meet/Find'))
const MeetView = lazy(() => import('libreon/Meet/View'))
const MeetCreate = lazy(() => import('libreon/Meet/Create'))

const Community = lazy(() => import('libreon/Community'))
const CommunityConnect = lazy(() => import('libreon/Community/Connect'))
const CommunityMentor = lazy(() => import('libreon/Community/Mentor'))
const CommunityChat = lazy(() => import('libreon/Community/Chat'))
const CommunityQuiz = lazy(() => import('libreon/Community/House/Quiz'))

//////////////////////////////////////
// const Org = lazy(() => import('libreon/Org'))

//////////////////////////////////////
const Ops = lazy(() => import('libreon/Ops'))
const OpsSummary = lazy(() => import('libreon/Ops/Summary'))
const OpsUser = lazy(() => import('libreon/Ops/User'))
const OpsPoints = lazy(() => import('libreon/Ops/Points'))
const OpsProject = lazy(() => import('libreon/Ops/Project'))
const OpsJourney = lazy(() => import('libreon/Ops/Journey'))
const OpsJourneyProfile = lazy(() => import('libreon/Ops/Journey/Profile'))
const OpsJourneyChallengeInputsOld = lazy(
  () => import('libreon/Ops/Journey/InputsOld')
)
const OpsJourneyChallengeInputs = lazy(() => import('libreon/Ops/Journey/Inputs'))
const OpsJourneyChallengeTree = lazy(
  () => import('libreon/Ops/Journey/ChallengeTree')
)
const OpsJourneyChallenges = lazy(() => import('libreon/Ops/Journey/Challenges'))
// const OpsJourneyReconcile = lazy(() => import('libreon/Ops/Journey/Reconcile'))

//////////////////////////////////////
const Sys = lazy(() => import('libreon/System'))
const SysEmailTemplate = lazy(() => import('libreon/System/EmailTemplate'))
const SysTags = lazy(() => import('libreon/System/Tags'))
const SysFileGroups = lazy(() => import('libreon/System/FileGroups'))

// const SysAdvisory = lazy(() => import('libreon/System/'))
// const SysTemplates = Outlet
// const DevTools = Outlet

//////////////////////////////////////
const Dev = lazy(() => import('libreon/Dev'))
const DevSchemas = lazy(() => import('libreon/Dev/Schemas'))
const DevStyles = lazy(() => import('libreon/Dev/Styles'))

//////////////////////////////////////
const Help = lazy(() => import('libreon/Help'))
// const OpsBilling = Outlet
// const OpsReports = Outlet
// const SystemAdmin = Outlet
// const SystemOperations = Outlet

////////////////////////////////////////////////////////////////////////////////
const authIdentified = {
  nav: {
    visible: ({ user: { isIdentified } }) => isIdentified
  }
}

const userMenu = (nav) => enrichAuthedMenu('user', nav)

const projectMenu = (nav) => enrichNoAuthMenu('explore', nav)

const exploreMenu = (nav) => enrichNoAuthMenu('explore', nav)
const communityMenu = (nav) => enrichNoAuthMenu('community', nav)

const projectEditMenu = { nav: tabMenus({ tabAdd: 'project_edit' }) }

function enrichNoAuthMenu(type, nav = {}) {
  return {
    nav: tabMenus({ tabAdd: [type], ...nav })
  }
}

function enrichAuthedMenu(type, nav = {}) {
  return {
    ...authIdentified,
    nav: tabMenus({ ...authIdentified.nav, tabAdd: [type], ...nav })
  }
}

const activeTab = { visible: ({ active }) => active }

const authMenu = (nav = {}) => ({
  nav: tabMenus(nav),
  pathTo: ({ base }) => '/auth/' + base
})

const meetEdit = { nav: tabMenus({ tabAdd: 'meet_edit' }) }

////////////////////////////////////////////////////////////////////////////////
export const { SITEMAP, ROUTES, MENUS, TABS } = buildConfigs([
  {
    id: 'home',
    title: 'Home', // future: feed? flow?
    nav: { tabAdd: { value: ['explore'] } },
    route: {
      lazy: Home,
      path: null,
      index: true
    }
  },

  /****************************** Auth ******************************/
  {
    id: 'auth',
    title: 'Signon',
    pathTo: ({ base }) => joinPath('/' + base),
    nav: {},
    route: {
      component: NavOutlet,
      path: 'auth'
    },
    children: [
      ...genNavPoint('Signon', AuthSignOn, { ...authMenu(), nav: {} }),
      ...genNavPoint(
        'Signout',
        AuthSignOut,
        authMenu({
          menuAdd: { order: 999, value: ['site'] },
          visible: ({ user: { isIdentified } }) => isIdentified
        })
      ),
      ...genNavPoint('Password', AuthPassword, {
        ...authMenu(),
        path: 'password/:code?',
        nav: {}
      })
    ]
  },

  /****************************** Titles/Project ******************************/
  ...genNavPoint('Titles', Project, {
    path: 'p/:projectId?',
    id: 'project',
    pathTo: ({ base }) => joinPath('/' + base),
    nav: tabMenus({ uses: 'explore' }),
    children: [
      ...genNavPoint('Titles', ProjectFind, {
        pathTo: ({ root }) => joinPath(root, '_', 'titles'),
        ...projectMenu({ visible: ({ user }) => user.id })
      }),
      ...genNavPoint('My Titles', ProjectMine, {
        idPath: 'mine',
        pathTo: ({ root }) => joinPath(root, '_', 'mine'),
        ...projectMenu({ visible: ({ user }) => user.id })
      }),
      ...genNavPoint('Create Title', ProjectCreate, {
        ...projectMenu(activeTab),
        idPath: 'create',
        pathTo: ({ root }) => joinPath(root, '_', 'create')
      }),
      ...genNavPoint('View', ProjectView, {
        ...projectMenu(activeTab),
        pathTo: ({ root, projectId, shortId, id, base }) =>
          joinPath(root, projectId || shortId || id, base)
      }),
      ...genNavPoint('Discuss', ProjectDiscussGeneral, {
        // note to self: to make "discuss/news" appear if project features are
        // enabled requires we push project features up to global state
        ...projectMenu(activeTab),
        path: 'd/:postId?',
        pathTo: ({ root, base, postId, originId, projectId, shortId, id }) =>
          joinPath(root, originId || projectId || shortId || id, base, postId)
      }),
      ...genNavPoint('Book Club', ProjectDiscussClub, {
        ...projectMenu(activeTab),
        path: 'club/:discussId?/:postId?',
        pathTo: ({
          root,
          base,
          discussId,
          postId,
          originId,
          projectId,
          shortId,
          id
        }) =>
          joinPath(
            root,
            shortId || originId || projectId || id || '_',
            base,
            discussId,
            postId
          )
      }),
      // ...genNavPoint('Updates', ProjectDiscuss, {
      //   // see note above about tab perms
      //   id: 'news',
      //   ...projectMenu(activeTab),
      //   path: 'news/:postId?',
      //   pathTo: ({ root, base, postId, originId, projectId, shortId, id }) =>
      //     joinPath(root, originId || projectId || shortId || id, base, postId)
      // }),

      // ...genNavPoint(
      //   'Gallery',
      //   ProjectGallery,
      //   projectMenu({ visible: ({ projectId }) => projectId })
      // ),

      ...genNavPoint('Edit', ProjectEdit, {
        ...projectMenu(activeTab),
        // ...projectMenu({ visible: ({tabId}) => 'project_edit' }),
        path: 'e',
        pathTo: ({ root, base, projectId }) => joinPath(root, projectId, base),
        children: [
          ...genNavPoint('Profile', ProjectProfile, projectEditMenu),
          ...genNavPoint('Guides', ProjectGuides, projectEditMenu),
          ...genNavPoint('Advisory', ProjectAdvisory, projectEditMenu),
          ...genNavPoint('Features', ProjectFeatures, projectEditMenu),
          // ...genNavPoint('Galleys', ProjectGalleys, projectEditMenu),
          //x ...genNavPoint('Files', ProjectFiles, projectEditMenu),
          ...genNavPoint('Members', ProjectMembers, projectEditMenu),
          // ...genNavPoint('Reviews', ProjectReviews, projectEditMenu),
          ...genNavPoint('Journey', ProjectJourneys, {
            ...projectEditMenu,
            path: 'journey',
            pathTo: ({ root, base, groupId }) => joinPath(root, base)
          }),
          ...genNavPoint('Inputs', ProjectChallengeInputGroup, {
            nav: tabMenus({ tabAdd: 'project_edit', ...activeTab }),
            path: 'inputs/:groupId/:openId?',
            pathTo: ({ root, base, groupId, openId, search = '' }) =>
              joinPath(root, base, groupId, openId) + '?' + search
          })
        ]
      }),
      genRedirect(({ base, projectId }) => joinPath(base, projectId, 'view'))
    ]
  }),

  // /******************************** PROVIDERS *********************************/
  // ...genNavPoint('Providers', Org, {
  //   path: 'o/:orgId?', // special keys: @create, @mine
  //   id: 'org',
  //   // do IDs like on Project with orgId || shortId || id
  //   pathTo: ({ base, orgId }) => joinPath('/' + base, orgId),
  //   nav: tabMenus({ tabHead: 'org' }),
  //   children: [
  //     ...genNavPoint('Discuss', OrgDiscuss, {
  //       ...orgMenu(),
  //       path: 'd/:postId?', // special keys: @admin, @create
  //       pathTo: ({ root, base, postId }) => joinPath(root, base, postId)
  //     }),
  //     ...genNavPoint('News', OrgNews, {
  //       ...orgMenu(),
  //       path: 'news/:postId?', // special keys: @admin, @create
  //       pathTo: ({ root, base, postId }) => joinPath(root, base, postId)
  //     }),
  //     ...genNavPoint('Reviews', OrgReviews, orgMenu()),
  //     ...genNavPoint('Titles', OrgTitles, orgMenu()),
  //     // ...genNavPoint('Portfolio', OrgFolio, {path: 'folio'}), // future admin of the whoel portoflio studiot type thing
  //     // ...genNavPoint('Portal', OrgPortal),
  //     ...genNavPoint('Title Series', OrgSeries, {
  //       ...orgMenu(),
  //       path: 'series/:seriesId?/:action?'
  //     }),
  //     ...genNavPoint('Billing', OrgBilling, orgMenu()),
  //     ...genNavPoint('Edit', OrgEdit, {
  //       ...orgMenu({
  //         ...orgMenu.nav,
  //       }),
  //       path: 'e',
  //       pathTo: ({ root, base }) => joinPath(root, base),
  //       children: [
  //         ...genNavPoint('Guides', OrgGuides),
  //         ...genNavPoint('Features', OrgFeatures),
  //         ...genNavPoint('Members', OrgMembers),
  //         ...genNavPoint('Galleys', OrgGalleys),
  //         ...genNavPoint('Site', OrgSite),
  //         ...genNavPoint('Files', OrgFiles),
  //         ...genNavPoint('Logs', OrgLogs)
  //       ]
  //     }),
  //     ...genNavPoint('Support', OrgSupport, orgMenu())
  //   ]
  // }),

  /****************************************************************************/
  ...genNavPoint('Explore', Explore, {
    path: 'exp',
    pathTo: () => '/p/_/titles',
    nav: tabMenus({
      tabHead: 'explore',
      menuAdd: 'app',
      icon: 'explore'
    }),
    children: [
      // ...genNavPoint('Galleys', ExploreGalleys, exploreMenu()),
      ...genNavPoint('Library', ExploreLearn, {
        ...exploreMenu(),
        id: 'topic',
        path: 'topic/:topicId?',
        pathTo: () => '/exp/topic',
        children: [
          ...genNavPoint('Edit', LearnEdit, {
            ...exploreMenu({
              visible: ({ topicId, active, user }) =>
                active && topicId && user.canGlobally('topic_edit')
            }),
            pathTo: ({ root, base, topicId, shortId, id }) =>
              joinPath(root, topicId || shortId || id || '_', base)
          }),
          ...genNavPoint('View', LearnView, {
            ...exploreMenu({
              visible: ({ active, user, topicId }) => active && topicId
            }),
            pathTo: ({ root, base, topicId, id, shortId }) =>
              joinPath(root, topicId || shortId || id || '_', base)
          }),
          ...genNavPoint('Create', LearnEdit, {
            ...exploreMenu({
              visible: ({ active, user }) =>
                active && user.canGlobally('topic_edit')
            }),
            pathTo: ({ root, base }) => joinPath(root, '_', base)
          })
        ]
      })
      // ...genNavPoint('Blogs', ExploreBlogs, authIdentified),
    ]
  }),

  /****************************************************************************/
  genRedirect(() => '/auth/signon', { id: 'old-signin', path: 'signin' }),
  genRedirect(() => '/auth/signout', { id: 'old-signout', path: 'signout' }),
  ...genNavPoint('Help', Help, {
    path: 'help/:page?',
    nav: tabMenus({ tabAdd: ['explore', 'help'], menuAdd: 'site' })
  }),

  /****************************************************************************/
  ...genNavPoint('Community', Community, {
    path: 'c',
    nav: tabMenus({
      tabHead: 'community',
      menuAdd: 'app',
      icon: 'community'
    }),
    title: 'Community',
    tab: 'Connect',
    children: [
      ...genNavPoint('Connect', CommunityConnect, communityMenu()),
      ...genNavPoint(
        'Mentor',
        CommunityMentor,
        communityMenu({
          /* user_edit for now, eventually a better way of calculating this */
          visible: ({ user }) => user.canGlobally('mentor')
        })
      ),
      ...genNavPoint('Chat', CommunityChat, communityMenu()),
      ...genNavPoint('House Quiz', CommunityQuiz, communityMenu()),
      genRedirect((args) => '/c/connect')
    ]
  }),

  /****************************************************************************/
  ...genNavPoint('Meetups', Meet, {
    path: 'm/:meetId?',
    id: 'meet',
    pathTo: ({ base }) => joinPath('/' + base),
    // nav: tabMenus({ uses: 'community' }),
    children: [
      ...genNavPoint('Meetups', MeetFind, {
        idPath: 'find',
        pathTo: ({ root }) => joinPath(root, '_', 'find')
        // ...communityMenu({ visible: ({ user }) => user.id })
      }),
      ...genNavPoint('My Meetups', MeetFind, {
        idPath: 'mine',
        pathTo: ({ root }) => joinPath(root, '_', 'mine')
        // ...communityMenu({ visible: ({ user }) => user.id })
      }),
      ...genNavPoint('Create Meetup', MeetCreate, {
        idPath: 'create',
        pathTo: ({ root }) => joinPath(root, '_', 'create')
        // ...communityMenu(activeTab)
      }),
      ...genNavPoint('View Meetup', MeetView, {
        id: 'view',
        path: 'view/:eventId?',
        pathTo: ({ root, meetId, base, eventId }) =>
          joinPath(root, meetId, base, eventId)
        // ...communityMenu(activeTab)
      }),
      // ...genNavPoint('Discuss', MeetDiscuss, {
      //   path: 'd/:postId?',
      //   pathTo: ({ root, base, postId, meetId, shortId, id }) =>
      //     joinPath(root, meetId || shortId || id, base, postId)
      //   ...communityMenu({
      //     visible: ({ meetId }) => meetId && meetId !== '_'
      //   }),
      // }),

      ...genNavPoint('Edit Meetup', MeetEdit, {
        // ...communityMenu(activeTab),
        idPath: 'edit',
        pathTo: ({ root, base, meetId, id, shortId }) =>
          joinPath(root, meetId || shortId || id, base),
        children: [
          ...genNavPoint('Profile', MeetEditProfile, meetEdit),
          // ...genNavPoint('Features', ...),
          ...genNavPoint('Files', MeetEditFiles, meetEdit),
          // ...genNavPoint('Hosts', communityHosts),
          ...genNavPoint('Events', MeetEditEventList, meetEdit),
          ...genNavPoint('Event', MeetEditEvent, {
            path: 'event/:eventId',
            nav: {
              ...meetEdit.nav,
              ...activeTab,
              name: ({ states }) => {
                // TODO: get name from states
                return 'Event'
              }
            },
            pathTo: ({ root, base, eventId, id, shortId }) =>
              joinPath(root, base, eventId || id || shortId)
          })
          //   label: (args) => {
          //     const {
          //       params: { eventId },
          //       state: { eventsD, longIds }
          //     } = args
          //     const ev = eventsD[longIds[eventId]]
          //     if (ev?.start) {
          //       return (<>{ev.startDate} {CLOSE}</>)
          //     }
          //     return (<>Event #{eventId} {CLOSE}</>)
          //   }
        ]
      })
    ]
  }),

  //////////////////////////////////////////////////////////////////////////////
  ...genNavPoint('User Preferences', User, {
    path: 'u/:userId?',
    id: 'user',
    nav: tabMenus({ uses: 'community' }),
    pathTo: ({ base, editUserId, userId, id, handle, user }) =>
      joinPath(
        '/' + base,
        editUserId || handle || user?.handle || id || userId || '_'
      ),
    children: [
      ...genNavPoint('My Profile', UserProfile, {
        idPath: 'profile',
        ...userMenu({
          menuAdd: 'site',
          tabAdd: ['community', 'user'],
          visible: ({ origin, active, user }) => {
            if (!user.isIdentified) return false
            return true
          }
        }),
        // override to avoid parent taking the wrong user handle
        pathTo: ({ root }) => joinPath(root, 'profile'),
        tab: 'User',
        subTab: ({ userId, user: { id, handle } }) => {
          if (userId === handle || userId === id) return 'Edit'
          return 'Profile'
        }
      }),
      ...genNavPoint('Contact', UserContact, userMenu()),
      ...genNavPoint('Subscriptions', UserSubscriptions, userMenu()),
      ...genNavPoint('LibMail', UserMessages, {
        path: 'libmail/:to?',
        pathTo: ({ root, to }) => joinPath(root, 'libmail', to),
        ...userMenu({ menuAdd: 'site' }),
        title: ({ user }) => {
          const sums = user?.messageSummary?.user_message || {}
          const sum = sums.posts + sums.comments || ''
          /* future: show 'new mail' */
          return 'LibMail ' + (sum ? `(${sum})` : '')
        }
      }),
      // ...genNavPoint('Posts', UserPosts, userMenu()),
      // ...genNavPoint('Karma', UserKarma, userMenu),
      ...genNavPoint('Account', UserAccount, userMenu()),
      ...genNavPoint('Delete', UserDelete, {
        ...userMenu({ visible: ({ curId, tabId }) => curId === tabId }),
        path: 'delete/:code'
      })
      // genRedirect(() => '../subscriptions') // , {name: 'subs', path: 'subs'})
      // ...genNavPoint('Support', UserSupport, userMenu),
      /*
        Coop/Share — TBD concept sharing alpha review/readers...
        Services   — a way to vette who you are... as an influencer
      */
    ]
  }),

  /****************************************************************************/
  ...genNavPoint('Ops', Ops, {
    idPath: 'ops',
    nav: tabMenus({
      menuAdd: 'app',
      icon: 'ops',
      visible: ({ user }) => user.canGlobally('user_edit')
    }),
    children: [
      ...genNavPoint('Ops', OpsSummary, {
        idPath: 'summary',
        nav: tabMenus({
          tabAdd: 'ops',
          visible: ({ user }) => user.canGlobally('user_edit')
        }),
        tab: () => 'Summary'
      }),
      ...genNavPoint('Users', OpsUser, {
        idPath: 'users',
        nav: tabMenus({ tabAdd: 'ops' })
      }),
      ...genNavPoint('Titles', OpsProject, {
        idPath: 'titles',
        nav: tabMenus({ tabAdd: 'ops' })
      }),
      ...genNavPoint('Journeys', OpsJourney, {
        id: 'journeys',
        tab: 'Journeys',
        path: 'journeys/:journeyId?',
        pathTo: ({ root, base, journeyId, id }) =>
          joinPath('/' + root, base, journeyId || id),
        nav: tabMenus({ tabAdd: 'ops' }),
        children: [
          ...genNavPoint('Journey', OpsJourneyProfile, {
            idPath: 'edit',
            nav: tabMenus({ tabAdd: 'ops_journeys_edit' })
          }),
          ...genNavPoint('Inputs(old)', OpsJourneyChallengeInputsOld, {
            idPath: 'inputsv1',
            nav: tabMenus({ tabAdd: 'ops_journeys_edit' })
          }),
          ...genNavPoint('Challenge Tree', OpsJourneyChallengeTree, {
            nav: tabMenus({ tabAdd: 'ops_journeys_edit' })
          }),
          ...genNavPoint('Inputs(v2)', OpsJourneyChallengeInputs, {
            id: 'inputs',
            path: 'inputs/:inputId?',
            nav: tabMenus({ tabAdd: 'ops_journeys_edit' })
          }),
          ...genNavPoint('Challenges v2', OpsJourneyChallenges, {
            idPath: 'challenges',
            nav: tabMenus({ tabAdd: 'ops_journeys_edit' })
          })
          // ...genNavPoint('Reconcile', OpsJourneyReconcile, {nav: tabMenus({tabAdd: 'ops_journeys_edit'})}),
        ]
      }),
      // ...genNavPoint('Orgs', SysProject, {
      //   idPath: 'titles',
      //   nav: tabMenus({ tabAdd: 'sys' })
      // }),
      ...genNavPoint('Points', OpsPoints, {
        nav: tabMenus({ tabAdd: 'ops' })
      }),
      genRedirect(() => 'summary')
    ]
  }),

  ...genNavPoint('System', Sys, {
    nav: tabMenus({
      menuAdd: 'app',
      icon: 'sys',
      visible: ({ user }) => user.canGlobally('user_edit')
    }),
    idPath: 'sys',
    children: [
      ...genNavPoint('Files', SysFileGroups, {
        nav: tabMenus({ tabAdd: 'sys' })
      }),
      ...genNavPoint('Email', SysEmailTemplate, {
        idPath: 'email',
        nav: tabMenus({ tabAdd: 'sys' })
      }),
      ...genNavPoint('Tags', SysTags, {
        id: 'tags',
        path: 'tags/:type?',
        nav: tabMenus({ tabAdd: 'sys' })
      }),
      genRedirect(() => 'files')
    ]
  }),

  ...genNavPoint('Dev', Dev, {
    nav: tabMenus({
      menuAdd: 'app',
      icon: 'dev',
      visible: ({ user }) => user.canGlobally('user_edit')
    }),
    children: [
      ...genNavPoint('Styles', DevStyles, { nav: tabMenus({ tabAdd: 'dev' }) }),
      ...genNavPoint('Schemas', DevSchemas, { nav: tabMenus({ tabAdd: 'dev' }) }),
      genRedirect(() => 'styles')
    ]
  }),

  /****************************************************************************/
  ...genNavPoint('doc0page', Doc0Page, {
    path: 'd/:page?/:edit?',
    pathTo: ({ navTab, base, page, edit }) => {
      if (navTab) return joinPath('/' + base)
      return joinPath('/' + base, page, edit ? 'edit' : undefined)
    },
    tab: 'Docs',
    nav: tabMenus({ tabAdd: 'ops' })
  }),

  // /****************************************************************************/
  // ...genNavPointPath('Operations', 'ops', SystemOperations, {
  //   children: [...genNavPoint('Reports', OpsReports), ...genNavPoint('Billing', OpsBilling)]
  // }),

  /****************************************************************************/
  ...genNavPoint('Terms', Terms, { path: '/terms/:path?' }),
  ...genNavPoint('CA', ContentAdvisory, { path: 'content-advisory' }),
  genRedirect(() => '/content-advisory', { id: 'CR', path: 'content-rating' }),
  ...genNavPoint('CG', ContentGuide, { path: 'content-guide' })
])

export function pathTo(routeId, args) {
  if (!SITEMAP[routeId])
    throw new Error(`pathTo() - cannot find section '${routeId}'`)
  return SITEMAP[routeId].pathTo(args)
}

// export function useSpecialParams(idKey) {
//   const params = useParams()
//   const id = params[idKey]
//   if (id === '_') {
//     return { blank: true, params }
//   } else if (id[0] === '@') {
//     return { special: id, params }
//   } else if (id) {
//     return { id, params }
//   }
//   return { params }
// }

// export function useHasId(idKey) //   const id = useParams()[idKey]
//   return id && id !== '_' && id[0] !== '@'
// }

console.log({ SITEMAP, MENUS, ROUTES, TABS })

export { ErrorBoundary }
